import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Clock, Loader2, Pencil } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEYS = ['breakfast', 'lunch', 'snacks', 'dinner'];
const LABEL = { breakfast: 'Breakfast', lunch: 'Lunch', snacks: 'Snacks', dinner: 'Dinner' };

/* Master-admin editor for the IST hours that define each meal section in sales reports. */
export const MealSectionHours = ({ canEdit, onSaved }) => {
  const [sections, setSections] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/report-settings/meal-sections`, { withCredentials: true })
      .then(({ data }) => setSections(data.sections)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await axios.put(`${API}/admin/report-settings/meal-sections`, { sections: draft }, { withCredentials: true });
      setSections(data.sections); setDraft(null); setMsg('✓ Saved');
      onSaved?.();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!sections) return null;
  const view = draft || sections;
  return (
    <div className="bg-card border border-border-light rounded-2xl p-4 mb-6" data-testid="meal-section-hours">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Meal section hours (IST)</h3>
        <div className="flex items-center gap-2">
          {msg && <span data-testid="meal-hours-msg" className={`text-xs ${msg.startsWith('✓') ? 'text-emerald-700' : 'text-red-600'}`}>{msg}</span>}
          {canEdit && !draft && (
            <button data-testid="meal-hours-edit" onClick={() => setDraft(sections)} className="text-xs px-3 py-1.5 rounded-lg border border-border-light flex items-center gap-1 hover:border-primary">
              <Pencil className="h-3.5 w-3.5" /> Edit hours
            </button>
          )}
          {draft && (
            <>
              <button data-testid="meal-hours-cancel" onClick={() => setDraft(null)} className="text-xs px-3 py-1.5 rounded-lg border border-border-light">Cancel</button>
              <button data-testid="meal-hours-save" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 rounded-lg bg-primary text-white flex items-center gap-1 disabled:opacity-50">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save
              </button>
            </>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {KEYS.map((k) => (
          <div key={k} className="rounded-xl border border-border-light px-3 py-2" data-testid={`meal-hours-${k}`}>
            <p className="text-xs font-medium text-text-secondary">{LABEL[k]}</p>
            {draft ? (
              <div className="flex items-center gap-1 mt-1">
                {['start', 'end'].map((f) => (
                  <input key={f} type="time" value={view[k][f]} data-testid={`meal-hours-${k}-${f}`}
                    onChange={(e) => setDraft({ ...draft, [k]: { ...draft[k], [f]: e.target.value } })}
                    className="w-full min-w-0 px-1.5 py-1 border border-border-light rounded text-xs bg-background" />
                ))}
              </div>
            ) : (
              <p className="text-sm font-semibold text-text-primary mt-0.5">{view[k].start} – {view[k].end}</p>
            )}
          </div>
        ))}
      </div>
      <p className="text-[11px] text-text-muted mt-2">Orders are placed in a section by the time they were placed. Orders outside all sections show as "Outside hours".</p>
    </div>
  );
};
